import { fetchContainerChildCounts } from "@/stores/documents/repository";
import { useEffect, useState } from "react";
import { reportDocumentTableFailure } from "./documentTableFailure";

import type { useTableDocumentKinds } from "./useTableDocumentKinds";
type Options = Pick<ReturnType<typeof useTableDocumentKinds>, "containerFiles">;
export function useTableContainerCounts(options: Options) {
  const { containerFiles } = options;
  // Fetch child counts for container files. `null` = not loaded yet for the
  // current container set, so a navigable `.zip` row shows no count rather than
  // claiming zero.
  const [loaded, setLoaded] = useState<{ key: string; counts: Map<string, number> } | null>(null);

  // Keyed on the joined ids, not the array: `documents` is rebuilt unmemoized
  // upstream, so `containerFiles` gets a fresh identity every render and an
  // array dep would reset-and-refetch in a loop. Include state so a container
  // reaching UNZIPPED refreshes the count after extraction completes.
  const containerIdsKey = containerFiles.map((doc) => doc.id).join(",");
  const containerIdsAndStatesKey = containerFiles
    .map((doc) => `${doc.id}:${doc.state ?? ""}`)
    .join(",");

  useEffect(() => {
    if (!containerIdsKey) return;
    let cancelled = false;
    fetchContainerChildCounts(containerIdsKey.split(","))
      .then((counts) => {
        if (!cancelled) setLoaded({ key: containerIdsAndStatesKey, counts });
      })
      .catch((err) => {
        reportDocumentTableFailure("container_counts_load", err);
        // Keep the unknown sentinel: a failed lookup must not claim the
        // container has zero documents.
        if (!cancelled) setLoaded(null);
      });
    return () => {
      cancelled = true;
    };
  }, [containerIdsAndStatesKey, containerIdsKey]);
  const containerChildCounts = !containerIdsKey
    ? EMPTY_COUNTS
    : loaded?.key === containerIdsAndStatesKey
      ? loaded.counts
      : null;
  return { containerChildCounts };
}

const EMPTY_COUNTS = new Map<string, number>();
